import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Code, ArrowUpRight } from 'lucide-react';
import CustomCursor from '../components/CustomCursor';
import Seo from '../components/Seo';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { useLanguage } from '../context/LanguageContext';
import './LandingPage.css';

// ──────────────────────────────────────────────────────────────
// HERO SCRIM DARKNESS — single tuning knob.
// 0 = no darkening (full video), 1 = full scrim strength.
// Raise it if the headline is hard to read; lower it to show more footage.
const HERO_SCRIM_OPACITY = 0.9;
// ──────────────────────────────────────────────────────────────

const LandingPage = () => {
  const { t, lang } = useLanguage();
  // Pick a lightweight hero video on phones (decided once, before first paint).
  const [isMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const heroVideoRef = useRef(null);
  const [loadHeroVideo, setLoadHeroVideo] = useState(false);
  const location = useLocation();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [counters, setCounters] = useState([0, 0, 0, 0]);
  const counterStarted = useRef(false);

  // Defer the hero video until the browser is idle (after first paint) so it
  // never competes with the LCP poster / FCP. The poster (video frame 1) shows
  // until then, so there's no visual change — just a faster paint.
  useEffect(() => {
    const start = () => setLoadHeroVideo(true);
    const ric = typeof window !== 'undefined' ? window.requestIdleCallback : null;
    const id = ric ? ric(start, { timeout: 2500 }) : setTimeout(start, 1200);
    return () => {
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  // Once the <source> is attached, load + play it (muted autoplay).
  useEffect(() => {
    if (!loadHeroVideo) return;
    const v = heroVideoRef.current;
    if (!v) return;
    v.load();
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [loadHeroVideo]);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;

    const handleScroll = () => {
      const sy = window.scrollY;

      // Parallax on hero layers (desktop only)
      if (!isMobile) {
        document.querySelectorAll('.parallax-layer').forEach(layer => {
          const speed = parseFloat(layer.dataset.speed || 0);
          layer.style.transform = `translateY(${sy * speed}px)`;
        });
      }

      // Section visibility
      document.querySelectorAll('.animate-on-scroll').forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          setVisibleSections(prev => new Set([...prev, section.id]));
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated number counters
  useEffect(() => {
    if (visibleSections.has('about') && !counterStarted.current) {
      counterStarted.current = true;
      const targets = [100, 5, 4, 21];
      const duration = 2000;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        setCounters(targets.map(t => Math.floor(t * ease)));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
  }, [visibleSections]);

  // When arriving from another page via "Projets" (/#ventures), scroll to it.
  useEffect(() => {
    if (location.hash === '#ventures') {
      const id = setTimeout(
        () => document.getElementById('ventures')?.scrollIntoView({ behavior: 'smooth' }),
        80,
      );
      return () => clearTimeout(id);
    }
  }, [location.hash]);

  return (
    <div className="landing-page">
      <Seo titleKey="seo.home.title" descKey="seo.home.description" path="/" />
      <CustomCursor />

      {/* Navigation */}
      <SiteNav />

      {/* Hero Section */}
      <section className="hero" id="hero">
        {/* Background video (replaces the old starfield). The dark base color
            on .hero stays behind everything as a fallback if the video fails. */}
        <video
          ref={heroVideoRef}
          className="hero-video"
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          poster="/bcg-hero-poster.jpg"
        >
          {loadHeroVideo && (
            <source
              src={isMobile ? '/bcg-hero-video-mobile.mp4' : '/bcg-hero-video-optimized.mp4'}
              type="video/mp4"
            />
          )}
        </video>

        {/* Dark scrim between the video and the text (darkest at the vertical
            center, lighter at the top). Overall darkness = HERO_SCRIM_OPACITY. */}
        <div className="hero-scrim" style={{ opacity: HERO_SCRIM_OPACITY }} />

        {/* Black gradient fade at the bottom so the hero dissolves into the next section. */}
        <div className="hero-fade-bottom" />
        <div className="hero-content">
          <h1 className="hero-title">{t('hero.title')}</h1>
          <h2 className="hero-subtitle">{t('hero.subtitle')}</h2>
        </div>
      </section>

      {/* About Section */}
      <section className="section about-section animate-on-scroll" id="about">
        <div className={`section-content ${visibleSections.has('about') ? 'visible' : ''}`}>
          <div className="section-label">{t('about.label')}</div>
          <h2 className="section-title">{t('about.title')}</h2>
          <p className="section-description">{t('about.description')}</p>
          <div className="stats-row">
            <div className="stat-item" data-testid="stat-team">
              <div className="stat-number">{counters[0]}+</div>
              <div className="stat-label">{t('about.teamMembers')}</div>
            </div>
            <div className="stat-item" data-testid="stat-divisions">
              <div className="stat-number">{counters[1]}</div>
              <div className="stat-label">{t('about.divisions')}</div>
            </div>
            <div className="stat-item" data-testid="stat-continents">
              <div className="stat-number">{counters[2]}</div>
              <div className="stat-label">{t('about.continents')}</div>
            </div>
            <div className="stat-item" data-testid="stat-revenue">
              <div className="stat-number">{lang === 'fr' ? `+${counters[3]} M€` : `€${counters[3]}M+`}</div>
              <div className="stat-label">{t('about.revenue')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Ventures Section */}
      <section className="section ventures-section animate-on-scroll" id="ventures">
        <div className={`section-content ${visibleSections.has('ventures') ? 'visible' : ''}`}>
          <div className="section-label">{t('ventures.label')}</div>
          <h2 className="section-title">{t('ventures.title')}</h2>

          <div className="ventures-bento">
            {/* TIER 1 — Featured E-Commerce */}
            <div className="venture-featured">
              <div className="venture-featured-text">
                <h3>{t('ventures.ecommerce.title')}</h3>
                <p>{t('ventures.ecommerce.desc')}</p>
              </div>
              <div className="brand-shelf" data-testid="ecommerce-logos">
                <a
                  href="https://popcard.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shelf-logo shelf-logo-link"
                  aria-label="Popcard"
                >
                  <img src="/popcard.avif" alt="Popcard" />
                  <ArrowUpRight size={14} className="shelf-logo-arrow" />
                </a>
                <span className="shelf-logo shelf-logo-blur" aria-hidden="true">
                  <img src="/decoy-1.svg" alt="" />
                </span>
                <span className="shelf-logo shelf-logo-blur" aria-hidden="true">
                  <img src="/harvey-ross.svg" alt="" />
                </span>
                <span className="shelf-logo shelf-logo-blur" aria-hidden="true">
                  <img src="/decoy-3.svg" alt="" />
                </span>
              </div>
            </div>

            {/* TIER 2 — Linked brand cards */}
            <div className="ventures-tier-linked">
              <a
                href="https://ecommercecapitalclub.com"
                target="_blank"
                rel="noopener noreferrer"
                className="brand-card"
              >
                <div className="brand-card-top">
                  <span className="brand-icon"><img src="/ecc.png" alt="E-Commerce Capital Club" /></span>
                  <ArrowUpRight size={18} className="brand-card-arrow" />
                </div>
                <h3>{t('ventures.club.title')}</h3>
                <p>{t('ventures.club.desc')}</p>
                <div className="brand-card-divider" />
                <p className="brand-card-proof">{t('ventures.club.proof')}</p>
              </a>

              <a
                href="https://sendexfulfillment.com"
                target="_blank"
                rel="noopener noreferrer"
                className="brand-card"
              >
                <div className="brand-card-top">
                  <span className="brand-icon"><img src="/sendex.png" alt="Sendex Fulfillment" /></span>
                  <ArrowUpRight size={18} className="brand-card-arrow" />
                </div>
                <h3>{t('ventures.sendex.title')}</h3>
                <p>{t('ventures.sendex.desc')}</p>
                <div className="brand-card-divider" />
                <p className="brand-card-proof">{t('ventures.sendex.proof')}</p>
              </a>
            </div>

            {/* TIER 3 — BCG Incubator (emphasized link) + SaaS (secondary) */}
            <div className="ventures-tier-units">
              <Link to="/incubator" className="venture-card unit-card unit-card-accent">
                <ArrowUpRight size={18} className="unit-card-arrow" />
                <span className="brand-icon"><img src="/bcg-incubator-mark.png" alt="BCG Incubator" /></span>
                <div className="venture-card-content">
                  <h3>{t('ventures.incubator.title')}</h3>
                  <p>{t('ventures.incubator.desc')}</p>
                  <span className="incubator-apply">{t('ventures.incubator.apply')}</span>
                </div>
              </Link>

              <div className="venture-card unit-card">
                <span className="brand-icon">
                  <Code size={24} strokeWidth={1.5} />
                </span>
                <div className="venture-card-content">
                  <h3>{t('ventures.saas.title')}</h3>
                  <p>{t('ventures.saas.desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section values-section animate-on-scroll" id="values">
        <div className={`section-content ${visibleSections.has('values') ? 'visible' : ''}`}>
          <div className="section-label">{t('values.label')}</div>
          <h2 className="section-title">{t('values.title')}</h2>

          <div className="values-grid">
            <div className="value-card">
              <h3>{t('values.loyalty.title')}</h3>
              <p>{t('values.loyalty.desc')}</p>
            </div>
            <div className="value-card">
              <h3>{t('values.speed.title')}</h3>
              <p>{t('values.speed.desc')}</p>
            </div>
            <div className="value-card">
              <h3>{t('values.efficiency.title')}</h3>
              <p>{t('values.efficiency.desc')}</p>
            </div>
            <div className="value-card">
              <h3>{t('values.ambition.title')}</h3>
              <p>{t('values.ambition.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section-wrapper animate-on-scroll" id="cta">
        <div className={`cta-section ${visibleSections.has('cta') ? 'visible' : ''}`}>
          <div className="section-content visible">
            <div className="section-label dark">{t('cta.label')}</div>
            <h2 className="section-title dark">{t('cta.title')}</h2>
            <p className="section-description dark cta-text">{t('cta.description')}</p>

            <a href="/careers" className="btn-cta">
              {t('cta.button')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
};

export default LandingPage;
